class CactusfyStorefront {
  constructor(root) {
    this.root = root;
    this.bindSingleProductNavigation();
    this.bindAnnouncements();
    this.bindHero();
    this.bindGallery();
    this.bindReviews();
    this.bindOffers();
    this.bindStickyBuy();
  }

  bindSingleProductNavigation() {
    const routeRoot = window.Shopify?.routes?.root || '/';
    const productUrl = `${routeRoot}products/cactusfy-glow-forever-serum`.replace(/\/+/g, '/');
    const isArabic = document.documentElement.lang?.toLowerCase().startsWith('ar');
    const normalizeLinks = () => document.querySelectorAll('a[href]').forEach((link) => {
        let path;
        try { path = new URL(link.href, window.location.origin).pathname.replace(/\/$/, ''); } catch (_) { return; }
        const hasCatalogLabel = /^(catalog|الكتالوج)$/i.test(link.textContent.trim());
        if (!path.endsWith('/collections/all') && !hasCatalogLabel) return;
        link.href = productUrl;
        if (hasCatalogLabel) {
          const label = isArabic ? 'تسوق جلو فوريفر' : 'Shop Glow Forever';
          const labelNode = link.querySelector('.menu-list__link-title, .menu-drawer__menu-item-text');
          if (labelNode) labelNode.textContent = label;
          else link.textContent = label;
        }
      });
    normalizeLinks();
    const observer = new MutationObserver(normalizeLinks);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  bindGallery() {
    this.root.querySelectorAll('[data-cactusfy-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-cactusfy-gallery-main]');
      const buttons = Array.from(gallery.querySelectorAll('[data-cactusfy-gallery-image]'));
      if (!main) return;
      buttons.forEach((button) => button.addEventListener('click', () => {
        buttons.forEach((item) => item.classList.toggle('is-active', item === button));
        main.src = button.dataset.cactusfyGalleryImage;
        main.alt = button.dataset.cactusfyGalleryAlt || '';
        main.classList.toggle('is-rotated-anticlockwise', button.dataset.cactusfyGalleryRotation === 'anticlockwise');
      }));
    });
  }

  bindHero() {
    this.root.querySelectorAll('[data-cactusfy-hero]').forEach((hero) => {
      const slides = Array.from(hero.querySelectorAll('[data-cactusfy-hero-slide]'));
      const dots = Array.from(hero.querySelectorAll('[data-cactusfy-hero-dot]'));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let index = 0;
      let autoplay;

      const show = (next) => {
        if (!slides.length) return;
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        dots.forEach((dot, dotIndex) => {
          const active = dotIndex === index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-selected', String(active));
        });
      };
      const stop = () => window.clearInterval(autoplay);
      const start = () => {
        stop();
        if (!reducedMotion.matches && !document.hidden && slides.length > 1) {
          autoplay = window.setInterval(() => show(index + 1), 5500);
        }
      };

      hero.querySelector('[data-cactusfy-hero-prev]')?.addEventListener('click', () => { show(index - 1); start(); });
      hero.querySelector('[data-cactusfy-hero-next]')?.addEventListener('click', () => { show(index + 1); start(); });
      dots.forEach((dot) => dot.addEventListener('click', () => { show(Number(dot.dataset.cactusfyHeroDot)); start(); }));
      hero.addEventListener('mouseenter', stop);
      hero.addEventListener('mouseleave', start);
      hero.addEventListener('focusin', stop);
      hero.addEventListener('focusout', () => window.setTimeout(() => { if (!hero.contains(document.activeElement)) start(); }, 0));
      reducedMotion.addEventListener('change', start);
      document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
      show(0);
      start();
    });
  }

  bindAnnouncements() {
    const bars = this.root.matches('[data-cactusfy-announcements]')
      ? [this.root]
      : Array.from(this.root.querySelectorAll('[data-cactusfy-announcements]'));
    bars.forEach((bar) => {
      const messages = Array.from(bar.querySelectorAll('[data-announcement-copy]')).map((node) => node.textContent.trim());
      const output = bar.querySelector('[data-announcement-output]');
      const position = bar.querySelector('[data-announcement-position]');
      if (!output || messages.length < 2) return;
      let index = 0;
      const render = () => {
        output.textContent = messages[index];
        output.style.animation = 'none';
        output.offsetHeight;
        output.style.animation = '';
        if (position) position.textContent = `${index + 1} / ${messages.length}`;
      };
      render();
      window.setInterval(() => { index = (index + 1) % messages.length; render(); }, 4500);
    });
  }

  bindReviews() {
    this.root.querySelectorAll('[data-review-shell]').forEach((shell) => {
      const track = shell.querySelector('[data-review-track]');
      if (!track) return;
      const cards = Array.from(track.querySelectorAll('.cactusfy-review'));
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let index = 0;
      let autoplay;

      const goTo = (nextIndex, behavior = 'smooth') => {
        if (!cards.length) return;
        index = (nextIndex + cards.length) % cards.length;
        track.scrollTo({ left: cards[index].offsetLeft - track.offsetLeft, behavior });
      };
      const stopAutoplay = () => window.clearInterval(autoplay);
      const startAutoplay = () => {
        stopAutoplay();
        if (prefersReducedMotion || cards.length < 2 || document.hidden) return;
        autoplay = window.setInterval(() => goTo(index + 1), 4500);
      };

      shell.querySelectorAll('[data-review-direction]').forEach((button) => {
        button.addEventListener('click', () => {
          goTo(index + Number(button.dataset.reviewDirection || 1));
          startAutoplay();
        });
      });
      shell.addEventListener('mouseenter', stopAutoplay);
      shell.addEventListener('mouseleave', startAutoplay);
      shell.addEventListener('focusin', stopAutoplay);
      shell.addEventListener('focusout', startAutoplay);
      track.addEventListener('pointerdown', stopAutoplay, { passive: true });
      document.addEventListener('visibilitychange', () => document.hidden ? stopAutoplay() : startAutoplay());
      startAutoplay();
    });
  }

  bindOffers() {
    this.root.querySelectorAll('[data-cactusfy-buy]').forEach((buy) => {
      const cards = Array.from(buy.querySelectorAll('[data-offer]'));
      const price = buy.querySelector('[data-selected-price]');
      const label = buy.querySelector('[data-selected-label]');
      const variantInput = buy.querySelector('[data-variant-input]');
      cards.forEach((card) => card.addEventListener('click', () => {
        cards.forEach((item) => item.setAttribute('aria-pressed', String(item === card)));
        if (price) price.textContent = card.dataset.price;
        if (label) label.textContent = card.dataset.label;
        if (variantInput && card.dataset.variantId) variantInput.value = card.dataset.variantId;
        buy.dispatchEvent(new CustomEvent('cactusfy:offer', { bubbles: true, detail: card.dataset }));
      }));
    });
  }

  bindStickyBuy() {
    const sticky = this.root.querySelector('[data-sticky-buy]');
    const mainButton = this.root.querySelector('[data-main-buy]');
    if (!sticky || !mainButton || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => sticky.classList.toggle('is-visible', !entry.isIntersecting), { threshold: 0 });
    observer.observe(mainButton);
    sticky.querySelector('button')?.addEventListener('click', () => mainButton.click());
    this.root.addEventListener('cactusfy:offer', (event) => {
      const label = sticky.querySelector('[data-sticky-label]');
      const price = sticky.querySelector('[data-sticky-price]');
      if (label) label.textContent = event.detail.label;
      if (price) price.textContent = event.detail.price;
    });
  }
}

const initializeCactusfy = (scope = document) => {
  scope.querySelectorAll('[data-cactusfy-root]:not([data-cactusfy-ready])').forEach((root) => {
    root.dataset.cactusfyReady = 'true';
    new CactusfyStorefront(root);
  });
};

document.addEventListener('DOMContentLoaded', () => initializeCactusfy());
document.addEventListener('shopify:section:load', (event) => initializeCactusfy(event.target));
